import checkIceCandidates from '@/modules/webrtc/agora/properties/checkIceCandidates';
import reset from '@/modules/webrtc/agora/properties/reset';
import configureAgoraLogs from '@/modules/webrtc/agora/properties/configureAgoraLogs';
import createCameraTracks from '@/modules/webrtc/agora/properties/createCameraTracks';
import createClient from '@/modules/webrtc/agora/properties/createClient';
import createMicrophoneTracks from '@/modules/webrtc/agora/properties/createMicrophoneTracks';
import createScreenTracks from '@/modules/webrtc/agora/properties/createScreenTracks';
import initVirtualBackground from './properties/initVirtualBackground';
import createVbackgroundTrack from '@/modules/webrtc/agora/properties/createVbackgroundTrack';
import disconnect from '@/modules/webrtc/agora/properties/disconnect';
import enableDualStream from './properties/enableDualStream';
import ensureVbPipelined from '@/modules/webrtc/agora/properties/ensureVbPipelined';
import filterDevice from '@/modules/webrtc/agora/properties/filterDevice';
import filterVideoTrack from '@/modules/webrtc/agora/properties/filterVideoTrack';
import getRTCServer from '@/modules/webrtc/agora/properties/getRTCServer';
import getMediaStream from '@/modules/webrtc/agora/properties/getMediaStream';
import isNetworkError from '@/modules/webrtc/agora/properties/isNetworkError';
import join from '@/modules/webrtc/agora/properties/join';
import joinChannel from '@/modules/webrtc/agora/properties/joinChannel';
import on from '@/modules/webrtc/agora/properties/on';
import publishStreamToChannel from '@/modules/webrtc/agora/properties/publishStreamToChannel';
import publishTracks from '@/modules/webrtc/agora/properties/publishTracks';
import remoteUserExists from '@/modules/webrtc/agora/properties/remoteUserExists';
import refreshRemoteStream from '@/modules/webrtc/agora/properties/refreshRemoteStream';
import setAudioDevice from '@/modules/webrtc/agora/properties/setAudioDevice';
import setAudioOutputDevice from '@/modules/webrtc/agora/properties/setAudioOutputDevice';
import setDeviceAvailability from '@/modules/webrtc/agora/properties/setDeviceAvailability';
import setEventListener from '@/modules/webrtc/agora/properties/setEventListener';
import setQualityOfNetwork from '@/modules/webrtc/agora/properties/setQualityOfNetwork';
import setQualityOnDownLinkSpeed from '@/modules/webrtc/agora/properties/setQualityOnDownLinkSpeed';
import setQualityOnUpLinkSpeed from '@/modules/webrtc/agora/properties/setQualityOnUpLinkSpeed';
import setOnEvent from '@/modules/webrtc/agora/properties/setOnEvent';
import setStreamConfig from '@/modules/webrtc/agora/properties/setStreamConfig';
import setVideoDevice from '@/modules/webrtc/agora/properties/setVideoDevice';
import setVideoEffect from '@/modules/webrtc/agora/properties/setVideoEffect';
import shareScreen from '@/modules/webrtc/agora/properties/shareScreen';
import toggleCamera from '@/modules/webrtc/agora/properties/toggleCamera';
import toggleMic from '@/modules/webrtc/agora/properties/toggleMic';
import unpublish from '@/modules/webrtc/agora/properties/unpublish';
import updateDeviceStream from '@/modules/webrtc/agora/properties/updateDeviceStream';
import playRemoteAudioTrack from '@/modules/webrtc/agora/properties/playRemoteAudioTrack';

import { NETWORK_QUALITY } from '@/modules/webrtc/agora/const';

/**
 * Agora.io's adapter for client
 */
class Adapter {
  constructor(Option) {
    this.remoteUsers = Option.remoteUsers;
    this.remoteUserId = Option.remoteUserId;
    this.rtc = this.createClient();

    // rtc options
    this.AppId = process.env.VUE_APP_AGORA_APP_ID;
    this.userId = Option.userId || null;
    this.channelToken = Option.credential; //in Agora, credential value is the channelToken
    this.channelName = '';

    this.events = [];
    this.localTracks = [];
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.localScreenTrack = null;
    this.localScreenAudioTrack = null;
    this.localVbackgroundTrack = null;
    this.remoteUserTrack = [];

    this.videoProfile = Option.videoProfile.profile;
    this.screenProfile = Option.screenShareProfile.profile;

    this.availableDevices = null;
    this.videoDevice = Option.videoDevice;
    this.audioDevice = Option.audioDevice;
    this.enableAudio = Option.enableAudio;
    this.enableVideo = Option.enableVideo;

    this.rtcServer = '';
    this.socket = Option.socket;

    this.videoEffect = Option.videoEffect;
    this.vbExtension = null;
    this.vbProcessor = null;
    this.vbProcessorReady = false;
    this.vbPipelined = false;
    this.vbInitPromise = null;
    this.virtualBackground = Option.virtualBgModel;

    // constraints of the stream
    this.constraints = {
      audio: true,
      video: true,
      screen: false,
      cameraId: null,
      microphoneId: null
    };

    this.downLinkNetworkQuality = NETWORK_QUALITY.excellent;
    this.upLinkNetworkQuality = NETWORK_QUALITY.excellent;
    this.switchDownLinkBuffer = [];
    this.switchUpLinkBuffer = [];
    this.lessonId = Option.lessonId;

    // Selected audio output (speaker) device. Applied to the remote audio track via
    // setPlaybackDevice, since Agora plays remote audio through its own internal element.
    this.audioOutputDevice = Option.audioOutputDevice || null;

    this.remoteUserRtc = null;

    this.vbInitPromise = this.initVirtualBackground();
  }
}

Object.assign(Adapter.prototype, checkIceCandidates);
Object.assign(Adapter.prototype, reset);
Object.assign(Adapter.prototype, configureAgoraLogs);
Object.assign(Adapter.prototype, createCameraTracks);
Object.assign(Adapter.prototype, createClient);
Object.assign(Adapter.prototype, createMicrophoneTracks);
Object.assign(Adapter.prototype, createScreenTracks);
Object.assign(Adapter.prototype, initVirtualBackground);
Object.assign(Adapter.prototype, createVbackgroundTrack);
Object.assign(Adapter.prototype, disconnect);
Object.assign(Adapter.prototype, enableDualStream);
Object.assign(Adapter.prototype, ensureVbPipelined);
Object.assign(Adapter.prototype, filterDevice);
Object.assign(Adapter.prototype, filterVideoTrack);
Object.assign(Adapter.prototype, getMediaStream);
Object.assign(Adapter.prototype, getRTCServer);
Object.assign(Adapter.prototype, isNetworkError);
Object.assign(Adapter.prototype, join);
Object.assign(Adapter.prototype, joinChannel);
Object.assign(Adapter.prototype, on);
Object.assign(Adapter.prototype, publishStreamToChannel);
Object.assign(Adapter.prototype, publishTracks);
Object.assign(Adapter.prototype, remoteUserExists);
Object.assign(Adapter.prototype, refreshRemoteStream);
Object.assign(Adapter.prototype, setAudioDevice);
Object.assign(Adapter.prototype, setAudioOutputDevice);
Object.assign(Adapter.prototype, setDeviceAvailability);
Object.assign(Adapter.prototype, setEventListener);
Object.assign(Adapter.prototype, setQualityOfNetwork);
Object.assign(Adapter.prototype, setQualityOnDownLinkSpeed);
Object.assign(Adapter.prototype, setQualityOnUpLinkSpeed);
Object.assign(Adapter.prototype, setOnEvent);
Object.assign(Adapter.prototype, setStreamConfig);
Object.assign(Adapter.prototype, setVideoDevice);
Object.assign(Adapter.prototype, setVideoEffect);
Object.assign(Adapter.prototype, shareScreen);
Object.assign(Adapter.prototype, toggleCamera);
Object.assign(Adapter.prototype, toggleMic);
Object.assign(Adapter.prototype, unpublish);
Object.assign(Adapter.prototype, updateDeviceStream);
Object.assign(Adapter.prototype, playRemoteAudioTrack);

export default Adapter;
